import { useState, useMemo } from "react";
import { Link, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import Logo from "@/components/Logo";
import { ArrowRight, Warning } from "@phosphor-icons/react";
import { isTeamDevice } from "@/lib/deviceRole";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [searchParams] = useSearchParams();
  const superseded = useMemo(() => searchParams.get("superseded") === "1", [searchParams]);
  // Demo credentials are pre-filled ONLY in the preview environment. Production
  // ships a clean login form (empty fields, no demo hint). Guarded by BOTH the
  // env flag AND the hostname — production builds must not leak this banner
  // even if REACT_APP_EMERGENT_PREVIEW=1 accidentally survives the deploy.
  const IS_PREVIEW = (() => {
    if (process.env.REACT_APP_EMERGENT_PREVIEW !== "1") return false;
    try {
      const host = (typeof window !== "undefined" && window.location && window.location.hostname) || "";
      return host === "localhost" || host === "127.0.0.1" || host.endsWith(".preview.emergentagent.com");
    } catch (_) {
      return false;
    }
  })();
  const [email, setEmail] = useState(IS_PREVIEW ? "admin@hellobyflow.com" : "");
  const [password, setPassword] = useState(IS_PREVIEW ? "admin123" : "");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  // Team-member devices (last login was an agent/supervisor) get a stripped
  // workspace-entry page — no marketing hero, no "Create a workspace" link,
  // no support chat widget. See lib/deviceRole.js for the rationale.
  const teamDevice = useMemo(() => isTeamDevice(), []);

  // `from=marketing` in the URL means the user arrived at /login by clicking
  // the "Log in" link on the public landing page. Team members (agents /
  // supervisors) MUST NOT enter through the marketing surface — only admins
  // and owners can. If an agent finishes login from this entrypoint, we
  // immediately sign them out and show a denial banner.
  const enteredFromMarketing = searchParams.get("from") === "marketing";
  const [deniedMsg, setDeniedMsg] = useState(
    searchParams.get("denied") === "team-via-marketing"
      ? "Team members must sign in from your workspace entry — not the marketing site. Ask your admin for your direct workspace link, or open the app from your bookmarks."
      : ""
  );

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setDeniedMsg("");
    setLoading(true);
    const r = await login(email, password);
    setLoading(false);
    if (r.ok) {
      // Email OTP gate — route to the verification screen.
      if (r.email_otp_challenge) {
        nav("/auth/mfa/verify", {
          state: {
            challenge_token: r.challenge_token,
            role: r.role,
            email_masked: r.email_masked,
            expires_in: r.expires_in,
            resend_available_in: r.resend_available_in,
            attempts_remaining: r.attempts_remaining,
            otp_dev_preview: r.otp_dev_preview,
            next: loc.state?.from || (r.role === "owner" ? "/owner" : "/app"),
          },
        });
        return;
      }
      // login() already populated AuthContext.user. Fetch fresh via api client
      // so we hit the same interceptors (CSRF, refresh) — cookies travel via withCredentials.
      try {
        const { data: me } = await api.get("/auth/me");
        // Marketing-entry guardrail: team roles can never sign in from the
        // marketing site. Sign them out and redirect with a clear banner.
        if (enteredFromMarketing && (me?.role === "agent" || me?.role === "supervisor")) {
          try { await api.post("/auth/logout"); } catch (_) { /* noop */ }
          nav("/login?denied=team-via-marketing", { replace: true });
          return;
        }
        if (me?.role === "owner") nav("/owner");
        else nav(loc.state?.from || "/app");
      } catch (_) {
        // /me failed — still ok, AuthContext.user is set; default to /app.
        nav(loc.state?.from || "/app");
      }
    } else setErr(r.error);
  };

  return (
    <div
      className="min-h-screen flex"
      data-testid="login-page"
      data-team-device={teamDevice ? "1" : "0"}
    >
      {/* Left: full-bleed brand panel (shared for team + admin) */}
      <div
        className="hidden lg:flex w-1/2 relative overflow-hidden items-center justify-end bg-white pr-32"
        data-testid="login-brand-panel"
      >
        {/* Big centered logo — uses the transparent-background variant so
            it merges seamlessly with the white panel (no halo, no card). */}
        <div className="relative flex flex-col items-center gap-5 max-w-[560px]">
          <img
            src="/brand/hbf-logo.png"
            alt="Hello by Flow"
            draggable={false}
            style={{ height: 360, width: "auto" }}
            data-testid="login-brand-logo"
          />
          <div className="font-mono text-[11px] tracking-[0.35em] text-[var(--accent)]">
            CALLS · MESSAGES · FLOWING TOGETHER
          </div>
        </div>
        <div className="absolute bottom-8 left-0 right-0 text-center font-mono text-[10px] tracking-widest text-[var(--text-muted)]">
          © 2026 HELLO BY FLOW
        </div>
      </div>

      {/* Right: form (both modes) */}
      <div className="w-full lg:w-1/2 flex flex-col px-12 py-10 bg-white">
        {/* Mobile-only logo (brand panel is hidden below lg) */}
        <div className="lg:hidden flex items-center mb-8" data-testid="login-logo-mobile">
          <Logo size={44} />
        </div>
        <div className="max-w-sm w-full lg:ml-0 lg:mr-auto my-auto">
          <div className="font-mono text-[11px] tracking-widest text-[var(--accent)] mb-3">/ LOG IN</div>
          <h1 className="font-display font-black text-[44px] leading-[1.0]">
            {teamDevice ? "Sign in to work." : "Welcome back."}
          </h1>

          {deniedMsg && (
            <div
              className="mt-4 flex items-start gap-3 rounded-lg border border-[#F5B199] bg-[#FEF2EC] p-4"
              data-testid="team-via-marketing-denied-banner"
              role="alert"
            >
              <Warning size={18} weight="fill" className="text-[var(--danger)] mt-[2px] shrink-0" />
              <div className="text-[13px] text-[var(--text-primary)] leading-snug">
                <div className="font-semibold">Access denied for team members from the marketing site.</div>
                <div className="text-[var(--text-secondary)] mt-1">{deniedMsg}</div>
              </div>
            </div>
          )}

          {superseded && (
            <div
              className="mt-4 flex items-start gap-3 rounded-lg border border-[#F5B199] bg-[#FEF2EC] p-4"
              data-testid="session-superseded-banner"
              role="alert"
            >
              <Warning size={18} weight="fill" className="text-[var(--danger)] mt-[2px] shrink-0" />
              <div className="text-[13px] text-[var(--text-primary)] leading-snug">
                <div className="font-semibold">Signed out — you signed in on another device.</div>
                <div className="text-[var(--text-secondary)] mt-1">
                  Your account only allows one active session at a time. If this wasn&apos;t you, change your password below.
                </div>
              </div>
            </div>
          )}

          {IS_PREVIEW && !teamDevice && (
            <p className="text-[14px] text-[var(--text-secondary)] mt-3" data-testid="login-demo-hint">
              Demo accounts pre-filled. <span className="font-mono text-[12px]">admin@hellobyflow.com / admin123</span>
            </p>
          )}

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <label className="label">{teamDevice ? "Username" : "Email or username"}</label>
              <input className="input" type="text" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email" autoComplete="username" placeholder={teamDevice ? "The username your admin gave you" : ""} />
            </div>
            <div>
              <label className="label">Password</label>
              <input className="input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password" />
            </div>
            {err && <div className="text-[13px] text-[var(--danger)] font-medium" data-testid="login-error">{err}</div>}
            <button type="submit" disabled={loading} className="btn-primary w-full flex items-center justify-center gap-2" data-testid="login-submit">
              {loading ? "Signing in..." : <>Sign in <ArrowRight size={16} /></>}
            </button>
          </form>

          <div className="mt-4 text-[13px]">
            <Link to="/forgot-password" className="text-[var(--accent)] font-semibold" data-testid="link-forgot-password">
              Forgot password?
            </Link>
          </div>
        </div>
        <div className="lg:hidden font-mono text-[11px] text-[var(--text-muted)] tracking-widest text-center pt-6">
          © 2026 HELLO BY FLOW
        </div>
      </div>
    </div>
  );
}
